// -----------------------------------------------------------------------------
// ROUTER — hash-based (#/p/BIM-024/issues/GF/GF-03) so the app works on any
// static host (Cloudflare Pages) without server rewrites.
//
// URL map (board numbers from the design canvas):
//   #/                                  A1 Projects
//   #/p/:code                           A2 Project dashboard
//   #/p/:code/activity                  A2.2 Activity (all modules, filters)
//   #/p/:code/issues                    A3 Issues Tracker overview
//   #/p/:code/issues/history[/:prefix]  A3 with A3.2 Publish history open (Activity report rows link here — Part 5)
//   #/p/:code/issues/:prefix            A4 Component summary
//   #/p/:code/issues/:prefix/:issue     A5 Issue detail      (issue = 'GF-03')
//   #/p/:code/setup                     A6 Project setup (Part 4) — roles with the Project setup switch
//   #/admin                             A7 Admin console › Projects (super admin only)
//   #/admin/members[/:code]             A7.2 Members (optionally filtered to one project) — super admin / Administrators
//                                       (v0.8.0, I1: no limited view; Project setup › Manage members instead)
//   #/forgot-password, #/set-password?… A0.1 / A0.2 (v0.8.0) — handled in App.tsx before sign-in (core/authLinks.ts)
//   #/admin/roles                       A7.1 Roles & access (super admin only)
//   #/admin/settings                    A7.5 App settings (root super admin only, v0.6.0)
//   #/settings                          Personal settings (profile menu)
//   #/notifications[/request|/closed]   A8 Notifications (v0.9.0): Pending action · Pending request · Closed (profile menu)
// A new module adds its own route kinds here and a matching page in App.tsx.
// -----------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
import { anyDirty, bypassing, requestLeave } from "./leaveGuard";

export type Route =
  | { name: "projects" }
  | { name: "dashboard"; code: string }
  | { name: "activity"; code: string }
  | { name: "issues"; code: string; history?: string }
  | { name: "component"; code: string; prefix: string }
  | { name: "issue"; code: string; prefix: string; issue: string }
  | { name: "setup"; code: string }
  | { name: "admin"; tab?: "projects" | "members" | "roles" | "settings"; code?: string }
  | { name: "settings" }
  | { name: "notifications"; tab?: "action" | "request" | "closed" }
  | { name: "notfound" };

const seg = (s: string) => encodeURIComponent(s);

/** Route → '#/…' link. */
export function href(r: Route): string {
  switch (r.name) {
    case "projects": return "#/";
    case "dashboard": return `#/p/${seg(r.code)}`;
    case "activity": return `#/p/${seg(r.code)}/activity`;
    case "issues": return `#/p/${seg(r.code)}/issues${r.history !== undefined ? "/history" + (r.history ? "/" + seg(r.history) : "") : ""}`;
    case "component": return `#/p/${seg(r.code)}/issues/${seg(r.prefix)}`;
    case "issue": return `#/p/${seg(r.code)}/issues/${seg(r.prefix)}/${seg(r.issue)}`;
    case "setup": return `#/p/${seg(r.code)}/setup`;
    case "admin": return r.tab === "members" ? `#/admin/members${r.code ? "/" + seg(r.code) : ""}` : r.tab === "roles" ? "#/admin/roles" : r.tab === "settings" ? "#/admin/settings" : "#/admin";
    case "settings": return "#/settings";
    case "notifications": return `#/notifications${r.tab && r.tab !== "action" ? "/" + r.tab : ""}`;
    default: return "#/";
  }
}

/** '#/…' → Route. Unknown paths → notfound. */
export function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean).map(decodeURIComponent);
  if (parts.length === 0) return { name: "projects" };
  if (parts[0] === "admin" && parts.length === 1) return { name: "admin" };
  if (parts[0] === "admin" && parts[1] === "members" && parts.length <= 3) return parts[2] ? { name: "admin", tab: "members", code: parts[2] } : { name: "admin", tab: "members" };
  if (parts[0] === "admin" && parts[1] === "roles" && parts.length === 2) return { name: "admin", tab: "roles" };
  if (parts[0] === "admin" && parts[1] === "settings" && parts.length === 2) return { name: "admin", tab: "settings" };
  if (parts[0] === "settings" && parts.length === 1) return { name: "settings" };
  if (parts[0] === "notifications" && parts.length <= 2 && (!parts[1] || parts[1] === "request" || parts[1] === "closed"))
    return parts[1] ? { name: "notifications", tab: parts[1] as "request" | "closed" } : { name: "notifications" };
  if (parts[0] === "p" && parts[1]) {
    const code = parts[1];
    if (parts.length === 2) return { name: "dashboard", code };
    if (parts[2] === "activity" && parts.length === 3) return { name: "activity", code };
    if (parts[2] === "setup" && parts.length === 3) return { name: "setup", code };
    if (parts[2] === "issues") {
      if (parts.length === 3) return { name: "issues", code };
      if (parts[3] === "history" && parts.length <= 5) return { name: "issues", code, history: parts[4] ?? "" };
      if (parts.length === 4) return { name: "component", code, prefix: parts[3] };
      if (parts.length === 5) return { name: "issue", code, prefix: parts[3], issue: parts[4] };
    }
  }
  return { name: "notfound" };
}

/** Project code of the current route, if the route is inside a project. */
export const projectCodeOf = (r: Route): string | null => (r.name !== "admin" && "code" in r ? r.code : null);

export function navigate(r: Route): void { window.location.hash = href(r); }

/** Parent page (fallback for Esc / Backspace / × when there is no earlier page in the app). Null on A1. */
export function parentRoute(r: Route): Route | null {
  switch (r.name) {
    case "issue": return { name: "component", code: r.code, prefix: r.prefix };
    case "component": return { name: "issues", code: r.code };
    case "issues": return { name: "dashboard", code: r.code };
    case "activity": return { name: "dashboard", code: r.code };
    case "setup": return { name: "dashboard", code: r.code };
    case "dashboard": case "admin": case "settings": case "notifications": case "notfound": return { name: "projects" };
    default: return null;
  }
}

// In-app page history (comment #8): Esc / Backspace / × go back to the page you came from.
const visited: string[] = [];
/** Record a page visit; a return to the previous page counts as going back (unit-tested). */
export function trackVisit(hash: string, stack: string[] = visited): void {
  if (steering) {                                 // backTo() on its way: pages passed on the way don't count
    if (hash === steering.target) { stack.length = steering.keep; steering = null; }
    return;
  }
  if (stack.length > 1 && stack[stack.length - 2] === hash) stack.pop();
  else if (stack[stack.length - 1] !== hash) stack.push(hash);
}
/** Which page a route shows, for remounting (App): opening / closing a pop-up that lives in the address (A3.2 Publish history)
 *  keeps the same page, so it doesn't reload and flash (v0.7.1, round 25 #17). Pure — unit-tested. */
export const pageKey = (r: Route, hash: string) => (r.name === "issues" ? href({ name: "issues", code: r.code }) : hash);

/** Go to a page WITHOUT adding a history step (A5 Esc → its component list, round 13 #5): Back from there
 *  then returns to the page before the issue, never to the issue again. */
export function replaceRoute(r: Route, stack: string[] = visited): void {
  const h = href(r);
  if (stack.length) stack[stack.length - 1] = h;
  if (typeof window !== "undefined") { replacing = true; window.location.replace(h); }
}
let replacing = false;                       // the next page change swapped the history entry (same position)
/** v0.9.2 #3: swap the current address for `hash` without a new history step (the sign-in page → the linked page). */
export function replaceHash(hash: string, stack: string[] = visited): void {
  if (stack.length) stack[stack.length - 1] = hash;
  if (typeof window !== "undefined") { replacing = true; window.location.replace(hash); }
}

/** A5 Esc → its component list (round 25 #18): when that list is the page right behind the issue(s) of this component you
 *  stepped through, how many steps back it is; 0 = it isn't (opened from elsewhere) → swap the issue for the list instead.
 *  Going BACK (not adding the list again) is what lets one more Esc reach the Issues Tracker. Pure — unit-tested. */
export function stepsBackToList(stack: string[], listHref: string): number {
  let n = 0;
  while (n < stack.length && stack[stack.length - 1 - n].startsWith(listHref + "/")) n++;
  return n > 0 && stack[stack.length - 1 - n] === listHref ? n : 0;
}
export function leaveIssue(r: { code: string; prefix: string }, stack: string[] = visited): void {
  // v0.7.3: no browser-history arithmetic (it misfired on slow machines). The issue page(s) are dropped from our own page
  // list and the issue's entry is swapped for the list; the next Esc then goes back by that list (goBack → backTo).
  const list: Route = { name: "component", code: r.code, prefix: r.prefix };
  const n = stepsBackToList(stack, href(list));
  if (n) stack.length -= n;                       // …, list, issue, issue → …, list
  replaceRoute(list, stack);
}

/** Round 16 #10: × / Esc in the Admin console leave the whole console — back to the page before it,
 *  however many tabs were opened (they are not steps); a direct link falls back to Projects. */
export function leaveAdmin(stack: string[] = visited): void {
  let n = 0;
  while (n < stack.length && /^#\/admin(\/|$)/.test(stack[stack.length - 1 - n])) n++;
  if (n > 0 && n < stack.length) {
    const target = stack[stack.length - 1 - n];
    stack.splice(stack.length - n, n - 1);
    // Safety: if the browser history was shorter than the in-app list (tabs revisited), land on the target anyway.
    const check = () => { window.removeEventListener("hashchange", check); if (/^#\/admin(\/|$)/.test(window.location.hash)) window.location.hash = target; };
    window.addEventListener("hashchange", check);
    window.history.go(-n);
    return;
  }
  navigate({ name: "projects" });
}

/** Go back to `target` (the page before this one in our list) with the browser's Back, so Back / Forward stay natural.
 *  The browser's own history can hold extra entries (an issue swapped for its list, ← / → between issues): if Back lands
 *  anywhere else — or nothing happens — the target replaces that entry. v0.7.3. */
let steering: { target: string; keep: number } | null = null;
export function backTo(target: string, stack: string[] = visited): void {
  steering = { target, keep: stack.length - 1 };
  const from = window.location.hash;
  const settle = () => { window.removeEventListener("hashchange", landed); clearTimeout(timer);
    if (steering && window.location.hash !== target) { replacing = true; window.location.replace(target); } };
  const landed = () => settle();
  window.addEventListener("hashchange", landed);
  const timer = setTimeout(() => { if (window.location.hash === from) settle(); else { window.removeEventListener("hashchange", landed); steering = null; } }, 1500);
  window.history.back();
}

/** Go back like the browser's Back button; with no earlier page → the parent page. False on A1. */
export function goBack(r: Route, stack: string[] = visited): boolean {
  if (r.name === "projects") return false;
  if (stack.length > 1) { backTo(stack[stack.length - 2], stack); return true; }
  const p = parentRoute(r);
  if (!p) return false;
  navigate(p);
  return true;
}

/** Current route; re-renders on hash change.
 *  v0.4.3: with unsaved edits anywhere (core/leaveGuard.ts) the app steps back to the page you were on and asks
 *  Save / Discard / Keep editing first — this catches links, the switcher, Back / Forward and Esc "go back".
 *  Each history entry is numbered (history.state.pos) so a blocked change can be undone in the right direction
 *  without leaving a duplicate entry behind (Back keeps working normally after "Keep editing"). */
export function useRoute(): Route {
  const shown = useRef(window.location.hash || "#/");
  const pos = useRef(0);
  const undoing = useRef(false);
  const afterUndo = useRef<(() => void) | null>(null);      // the question waits until the browser is back on this page
  const [route, setRoute] = useState(() => {
    const st = window.history.state as { pos?: number } | null;
    if (typeof st?.pos === "number") pos.current = st.pos; else window.history.replaceState({ pos: 0 }, "");
    trackVisit(shown.current);
    return parseHash(shown.current);
  });
  useEffect(() => {
    const go = (hash: string) => {
      const st = window.history.state as { pos?: number } | null;
      if (typeof st?.pos === "number") pos.current = st.pos;
      else { if (!replacing) pos.current += 1; window.history.replaceState({ pos: pos.current }, ""); }   // new entry (or swapped)
      replacing = false;
      shown.current = hash; trackVisit(hash); setRoute(parseHash(hash));
    };
    const on = (ev: HashChangeEvent) => {
      const target = new URL(ev.newURL).hash || "#/";          // the address this change was for (not a later one)
      if (undoing.current) {
        if (target === shown.current) { undoing.current = false; const ask = afterUndo.current; afterUndo.current = null; ask?.(); }
        return;
      }
      if (target === shown.current) return;
      if (bypassing() || !anyDirty()) return go(target);
      // stay put while asking: undo the step (Back / Forward → the other way; a new link → back off it)
      const st = window.history.state as { pos?: number } | null;
      undoing.current = true;
      if (replacing) {                                                           // a swapped entry: swap it back
        replacing = false;
        afterUndo.current = () => requestLeave(() => { replacing = true; window.location.replace(target); });
        window.location.replace(shown.current);
        return;
      }
      afterUndo.current = () => requestLeave(() => { window.location.hash = target; });
      window.history.go(typeof st?.pos === "number" ? pos.current - st.pos : -1);
    };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}
